import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  TrophyIcon, 
  UsersIcon, 
  CalendarIcon, 
  MapPinIcon,
  ClockIcon,
  PhotoIcon,
  StarIcon,
  PhoneIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { 
  TrophyIcon as TrophyIconSolid,
  StarIcon as StarIconSolid 
} from '@heroicons/react/24/solid';
import PhotoGallery from '../../components/PhotoGallery';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const SportDetailPage = () => {
  const { slug } = useParams();
  const [sport, setSport] = useState(null);
  const [players, setPlayers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [gameResults, setGameResults] = useState([]);
  const [news, setNews] = useState([]);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    fetchSportData();
  }, [slug]);

  const fetchSportData = async () => {
    try {
      setLoading(true);
      
      // Fetch sport details
      const sportResponse = await fetch(`${API_BASE_URL}/sports/${slug}`);
      if (sportResponse.ok) {
        const sportData = await sportResponse.json();
        const sport = sportData.sport;
        
        // Create images array from available image data
        if (sport.signedUrl || sport.featuredImage) {
          sport.images = [sport.signedUrl || sport.featuredImage];
        } else {
          sport.images = [];
        }
        
        setSport(sport);
        

        
        // Fetch related data
        await Promise.all([
          fetchPlayers(sportData.sport.id),
          fetchSchedules(sportData.sport.id),
          fetchGameResults(sportData.sport.id),
          fetchNews(sportData.sport.id),
          fetchMedia(sportData.sport.id)
        ]);
      } else {
        // Fallback to mock data if API fails
        loadMockData();
      }
    } catch (error) {
      console.error('Error fetching sport data:', error);
      loadMockData();
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayers = async (sportId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/players?sportId=${sportId}`);
      if (response.ok) {
        const data = await response.json();
        setPlayers(data.players || []);
      }
    } catch (error) {
      console.error('Error fetching players:', error);
    }
  };

  const fetchSchedules = async (sportId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/schedules/sport/${sportId}?upcoming=false`);
      if (response.ok) {
        const data = await response.json();
        if (data.schedules && Array.isArray(data.schedules)) {
          setSchedules(data.schedules);
        } else {
          setSchedules([]);
        }
      } else {
        setSchedules([]);
      }
    } catch (error) {
      console.error('Error fetching schedules:', error);
      setSchedules([]);
    }
  };

  const fetchGameResults = async (sportId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/game-results?sportId=${sportId}&limit=10`);
      if (response.ok) {
        const data = await response.json();
        setGameResults(data.results || []);
      }
    } catch (error) {
      console.error('Error fetching game results:', error);
    }
  };

  const fetchNews = async (sportId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/news?sportId=${sportId}&limit=5`);
      if (response.ok) {
        const data = await response.json();
        setNews(data.news || []);
      }
    } catch (error) {
      console.error('Error fetching news:', error);
    }
  };

  const fetchMedia = async (sportId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/media/sport/${sportId}?limit=12`);
      if (response.ok) {
        const data = await response.json();
        setMedia(data.media || []);
      }
    } catch (error) {
      console.error('Error fetching media:', error);
    }
  };

  const loadMockData = () => {
    // Mock data for development/fallback
    const mockSport = {
      id: 1,
      name: 'Football',
      slug: 'football',
      description: 'Competitive tackle football program building teamwork and athletic excellence.',
      season: 'fall',
      gender: 'boys',
      isActive: true,
      coachName: 'Coach Johnson',
      coachEmail: 'coach.johnson@eastendprep.edu',
      coachPhone: '(555) 123-4567',
      coachBio: 'Coach Johnson has been leading our football program for over 10 years, bringing extensive experience and a passion for developing young athletes.',
      teamDescription: 'Our football team emphasizes discipline, teamwork, and academic excellence. We compete in the regional high school league and have a strong tradition of success.',
      rules: 'All players must maintain a 2.5 GPA minimum. Practice attendance is mandatory. Equipment must be properly maintained.',
      images: ['/api/placeholder/800/400', '/api/placeholder/800/400', '/api/placeholder/800/400']
    };
    
    setSport(mockSport);
    
    // Mock players data
    const mockPlayers = [
      {
        id: 1,
        firstName: 'John',
        lastName: 'Smith',
        jerseyNumber: 12,
        position: 'Quarterback',
        grade: '12',
        height: '6\'2"',
        weight: 185,
        isCaptain: true,
        isStarter: true,
        profileImage: '/api/placeholder/150/150',
        bio: 'Senior quarterback with excellent leadership skills and strong arm.'
      },
      {
        id: 2,
        firstName: 'Mike',
        lastName: 'Johnson',
        jerseyNumber: 24,
        position: 'Running Back',
        grade: '11',
        height: '5\'10"',
        weight: 175,
        isCaptain: false,
        isStarter: true,
        profileImage: '/api/placeholder/150/150',
        bio: 'Junior running back known for speed and agility.'
      }
    ];
    setPlayers(mockPlayers);
    
    // Mock schedules
    const mockSchedules = [
      {
        id: 1,
        title: 'vs. Central High',
        type: 'game',
        startDate: '2024-02-15',
        startTime: '19:00',
        location: 'Home Stadium',
        opponent: 'Central High',
        isHome: true
      },
      {
        id: 2,
        title: 'Practice',
        type: 'practice',
        startDate: '2024-02-12',
        startTime: '15:30',
        location: 'Practice Field',
        isHome: true
      }
    ];
    setSchedules(mockSchedules);
    
    // Mock game results
    const mockResults = [
      {
        id: 1,
        opponent: 'West High',
        gameDate: '2024-01-20',
        ourScore: 28,
        opponentScore: 14,
        result: 'win',
        isHome: true,
        location: 'Home Stadium'
      }
    ];
    setGameResults(mockResults);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getSeasonRecord = () => {
    const wins = gameResults.filter(game => game.result === 'win').length;
    const losses = gameResults.filter(game => game.result === 'loss').length;
    const ties = gameResults.filter(game => game.result === 'tie').length;
    return { wins, losses, ties };
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: TrophyIcon },
    { id: 'roster', name: 'Roster', icon: UsersIcon },
    { id: 'schedule', name: 'Schedule', icon: CalendarIcon },
    { id: 'gallery', name: 'Gallery', icon: PhotoIcon }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!sport) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Sport Not Found</h1>
          <Link to="/sports" className="text-blue-600 hover:text-blue-800">
            Back to Sports
          </Link>
        </div>
      </div>
    );
  }

  const record = getSeasonRecord();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-blue-900 to-blue-700 text-white">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <nav className="flex mb-4" aria-label="Breadcrumb">
                <ol className="flex items-center space-x-2">
                  <li>
                    <Link to="/" className="text-blue-200 hover:text-white">
                      Home
                    </Link>
                  </li>
                  <li className="text-blue-200">/</li>
                  <li>
                    <Link to="/sports" className="text-blue-200 hover:text-white">
                      Sports
                    </Link>
                  </li>
                  <li className="text-blue-200">/</li>
                  <li className="text-white font-medium">{sport.name}</li>
                </ol>
              </nav>
              
              <h1 className="text-4xl lg:text-5xl font-bold mb-4">{sport.name}</h1>
              <p className="text-xl text-blue-100 mb-6">{sport.description}</p>
              
              <div className="flex flex-wrap gap-4 mb-6">
                <div className="bg-white bg-opacity-20 rounded-lg px-4 py-2">
                  <div className="text-sm text-blue-100">Season</div>
                  <div className="font-semibold capitalize">{sport.season}</div>
                </div>
                <div className="bg-white bg-opacity-20 rounded-lg px-4 py-2">
                  <div className="text-sm text-blue-100">Players</div>
                  <div className="font-semibold">{players.length}</div>
                </div>
              </div>
            </div>
            
            {sport.images && sport.images.length > 0 && (
              <div className="relative">
                <img
                  src={sport.images[selectedImageIndex]}
                  alt={sport.name}
                  className="w-full h-64 lg:h-80 object-cover rounded-lg shadow-lg"
                />
                {sport.images.length > 1 && (
                  <div className="flex justify-center mt-4 space-x-2">
                    {sport.images.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImageIndex(index)}
                        className={`w-3 h-3 rounded-full ${
                          index === selectedImageIndex ? 'bg-white' : 'bg-white bg-opacity-50'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Content Sections */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Coach Information */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Coaching Staff</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-6">
                  {/* Head Coach */}
                  {sport.coach && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {sport.coach.firstName} {sport.coach.lastName}
                        <span className="text-sm text-gray-500 ml-2">(Head Coach)</span>
                      </h3>
                      <div className="space-y-2">
                        {sport.coach.email && (
                          <div className="flex items-center text-gray-600">
                            <EnvelopeIcon className="h-5 w-5 mr-2" />
                            <a href={`mailto:${sport.coach.email}`} className="hover:text-blue-600">
                              {sport.coach.email}
                            </a>
                          </div>
                        )}
                        {sport.coach.phone && (
                          <div className="flex items-center text-gray-600">
                            <PhoneIcon className="h-5 w-5 mr-2" />
                            <a href={`tel:${sport.coach.phone}`} className="hover:text-blue-600">
                              {sport.coach.phone}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Assistant Coaches */}
                  {sport.assistantCoaches && sport.assistantCoaches.length > 0 && (
                    <div>
                      <h4 className="text-md font-semibold text-gray-900 mb-3">Assistant Coaches</h4>
                      <div className="space-y-3">
                        {sport.assistantCoaches.map((coach, index) => (
                          <div key={index} className="border-l-2 border-blue-200 pl-4">
                            <h5 className="font-medium text-gray-900">{coach.name}</h5>
                            {coach.title && (
                              <p className="text-sm text-gray-600">{coach.title}</p>
                            )}
                            {coach.email && (
                              <div className="flex items-center text-gray-600 mt-1">
                                <EnvelopeIcon className="h-4 w-4 mr-2" />
                                <a href={`mailto:${coach.email}`} className="text-sm hover:text-blue-600">
                                  {coach.email}
                                </a>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Team Information</h4>
                  <p className="text-gray-600">{sport.teamDescription}</p>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                <UsersIcon className="h-12 w-12 text-blue-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Team Size</h3>
                <p className="text-3xl font-bold text-blue-600">{players.length}</p>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-6 text-center">
                <CalendarIcon className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Upcoming Games</h3>
                <p className="text-3xl font-bold text-blue-600">
                  {schedules.filter(s => s.type === 'game').length}
                </p>
              </div>
            </div>

            {/* Rules & Regulations */}
            {sport.rules && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Rules & Regulations</h2>
                <div className="prose max-w-none text-gray-600">
                  <p>{sport.rules}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Roster Tab */}
        {activeTab === 'roster' && (
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Team Roster</h2>
              <p className="text-gray-600 mt-2">{players.length} players</p>
            </div>
            
            {players.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
                {players.map((player) => (
                  <div key={player.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-center space-x-4">
                      <div className="relative">
                        <img
                          src={player.profileImage || '/api/placeholder/80/80'}
                          alt={`${player.firstName} ${player.lastName}`}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                        {player.isCaptain && (
                          <StarIconSolid className="absolute -top-1 -right-1 h-6 w-6 text-yellow-500" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">
                          {player.firstName} {player.lastName}
                        </h3>
                        <p className="text-sm text-gray-600">#{player.jerseyNumber}</p>
                        <p className="text-sm text-gray-600">{player.position}</p>
                      </div>
                    </div>
                    
                    {(player.height || player.weight) && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <div className="flex justify-between text-sm text-gray-600">
                          {player.height && <span>Height: {player.height}</span>}
                          {player.weight && <span>Weight: {player.weight} lbs</span>}
                        </div>
                      </div>
                    )}
                    
                    {player.bio && (
                      <div className="mt-3 pt-3 border-t border-gray-100">
                        <p className="text-sm text-gray-600">{player.bio}</p>
                      </div>
                    )}
                    
                    <div className="mt-3 flex space-x-2">
                      {player.isCaptain && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Captain
                        </span>
                      )}
                      {player.isStarter && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Starter
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500">
                <UsersIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No players found for this team.</p>
              </div>
            )}
          </div>
        )}

        {/* Schedule Tab */}
        {activeTab === 'schedule' && (
          <div className="space-y-6">
            {/* Upcoming Games */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900">Upcoming Schedule</h2>
              </div>
              
              {schedules.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {schedules.map((schedule) => (
                    <div key={schedule.id} className="p-6 hover:bg-gray-50">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                          <div className={`p-3 rounded-full ${
                            schedule.type === 'game' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                          }`}>
                            {schedule.type === 'game' ? (
                              <TrophyIcon className="h-6 w-6" />
                            ) : (
                              <ClockIcon className="h-6 w-6" />
                            )}
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">{schedule.title}</h3>
                            <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                              <span className="flex items-center">
                                <CalendarIcon className="h-4 w-4 mr-1" />
                                {formatDate(schedule.startDate)}
                              </span>
                              <span className="flex items-center">
                                <ClockIcon className="h-4 w-4 mr-1" />
                                {formatTime(schedule.startTime)}
                              </span>
                              {schedule.location && (
                                <span className="flex items-center">
                                  <MapPinIcon className="h-4 w-4 mr-1" />
                                  {schedule.location}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            schedule.type === 'game' 
                              ? 'bg-blue-100 text-blue-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {schedule.type === 'game' ? 'Game' : 'Practice'}
                          </span>
                          {schedule.isHome !== undefined && (
                            <div className="text-sm text-gray-500 mt-1">
                              {schedule.isHome ? 'Home' : 'Away'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-6 text-center text-gray-500">
                  <CalendarIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No upcoming events scheduled.</p>
                </div>
              )}
            </div>
          </div>
        )}



        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <div className="bg-white rounded-lg shadow-sm">
            <PhotoGallery sportId={sport?.id} sportName={sport?.name} />
          </div>
        )}
      </div>
    </div>
  );
};

export default SportDetailPage;